import { create } from "zustand";
import { getPlatformService } from "../../platform";
import { useSettingsStore } from "../../store/settings-store";
import type { ThemeColorScheme, ThemeKind } from "../contracts";
import {
    compileProfileStyleVariables,
    KNOWN_PROFILE_STYLE_VARIABLES,
} from "./profile-resolver";
import {
    applyStyleVariables,
    compileThemeStyleVariables,
    KNOWN_THEME_STYLE_VARIABLES,
} from "./theme-compiler";
import {
    DEFAULT_COLOR_THEME_ID,
    DEFAULT_CONTENT_PROFILE_ID,
    DEFAULT_FILE_ICON_THEME_ID,
    DEFAULT_PRESENTATION_PROFILE_ID,
    DEFAULT_PRODUCT_ICON_THEME_ID,
    resolveThemeKind,
} from "./theme-resolver";

export interface ThemeSettingsInput {
    colorTheme?: unknown;
    productIconTheme?: unknown;
    fileIconTheme?: unknown;
    contentProfile?: unknown;
    presentationProfile?: unknown;
}

export interface ThemeRuntimeState {
    colorTheme: string;
    productIconTheme: string;
    fileIconTheme: string;
    contentProfile: string;
    presentationProfile: string;
    resolvedThemeId: string;
    resolvedThemeKind: ThemeKind;
    resolvedColorScheme: ThemeColorScheme;
    resolvedHighContrast: boolean;
    applySettings: (settings: ThemeSettingsInput) => void;
    toggleColorTheme: () => Promise<void>;
}

const DEFAULT_THEME_SETTINGS = {
    colorTheme: DEFAULT_COLOR_THEME_ID,
    productIconTheme: DEFAULT_PRODUCT_ICON_THEME_ID,
    fileIconTheme: DEFAULT_FILE_ICON_THEME_ID,
    contentProfile: DEFAULT_CONTENT_PROFILE_ID,
    presentationProfile: DEFAULT_PRESENTATION_PROFILE_ID,
};

export function resolveThemeState(
    settings: Omit<
        ThemeRuntimeState,
        | "resolvedThemeId"
        | "resolvedThemeKind"
        | "resolvedColorScheme"
        | "resolvedHighContrast"
        | "applySettings"
        | "toggleColorTheme"
    >,
): Pick<
    ThemeRuntimeState,
    | "resolvedThemeId"
    | "resolvedThemeKind"
    | "resolvedColorScheme"
    | "resolvedHighContrast"
> {
    const resolvedThemeId = settings.colorTheme;
    const resolvedThemeKind = resolveThemeKind(resolvedThemeId);
    return {
        resolvedThemeId,
        resolvedThemeKind,
        resolvedColorScheme: resolvedThemeKind.includes("dark") ? "dark" : "light",
        resolvedHighContrast: resolvedThemeKind.startsWith("hc"),
    };
}

export const useThemeStore = create<ThemeRuntimeState>((set, get) => {
    const initialResolved = resolveThemeState(DEFAULT_THEME_SETTINGS);

    function updateThemeState(
        partialSettings?: Partial<typeof DEFAULT_THEME_SETTINGS>,
    ): void {
        const current = get();
        const nextSettings = {
            colorTheme: partialSettings?.colorTheme ?? current.colorTheme,
            productIconTheme:
                partialSettings?.productIconTheme ?? current.productIconTheme,
            fileIconTheme: partialSettings?.fileIconTheme ?? current.fileIconTheme,
            contentProfile: partialSettings?.contentProfile ?? current.contentProfile,
            presentationProfile:
                partialSettings?.presentationProfile ?? current.presentationProfile,
        };
        const resolved = resolveThemeState(nextSettings);

        set({
            ...nextSettings,
            ...resolved,
        });
        syncDocumentTheme({
            productIconTheme: nextSettings.productIconTheme,
            fileIconTheme: nextSettings.fileIconTheme,
            contentProfile: nextSettings.contentProfile,
            presentationProfile: nextSettings.presentationProfile,
            resolvedColorScheme: resolved.resolvedColorScheme,
            resolvedHighContrast: resolved.resolvedHighContrast,
            resolvedThemeId: resolved.resolvedThemeId,
        });
    }

    return {
        ...DEFAULT_THEME_SETTINGS,
        ...initialResolved,
        applySettings(settings) {
            updateThemeState(normalizeThemeSettings(settings));
        },
        async toggleColorTheme() {
            const current = get();
            const resolvedKind = resolveThemeKind(current.colorTheme);
            const isDark = resolvedKind.includes("dark");
            const nextThemeId = isDark
                ? DEFAULT_COLOR_THEME_ID
                : "magicmd.workbench-dark";

            const patch: Record<string, unknown> = {
                "editor.appearance.colorTheme": nextThemeId,
            };

            updateThemeState({ colorTheme: nextThemeId });
            await persistThemeUserSettings(patch);
        },
    };
});

function normalizeThemeSettings(
    settings: ThemeSettingsInput,
): Partial<typeof DEFAULT_THEME_SETTINGS> {
    const next: Partial<typeof DEFAULT_THEME_SETTINGS> = {};

    if (typeof settings.colorTheme === "string" && settings.colorTheme) {
        next.colorTheme = settings.colorTheme;
    }
    if (
        typeof settings.productIconTheme === "string" &&
        settings.productIconTheme
    ) {
        next.productIconTheme = settings.productIconTheme;
    }
    if (typeof settings.fileIconTheme === "string" && settings.fileIconTheme) {
        next.fileIconTheme = settings.fileIconTheme;
    }
    if (typeof settings.contentProfile === "string" && settings.contentProfile) {
        next.contentProfile = settings.contentProfile;
    }
    if (
        typeof settings.presentationProfile === "string" &&
        settings.presentationProfile
    ) {
        next.presentationProfile = settings.presentationProfile;
    }

    return next;
}

function syncDocumentTheme(params: {
    productIconTheme: string;
    fileIconTheme: string;
    contentProfile: string;
    presentationProfile: string;
    resolvedColorScheme: ThemeColorScheme;
    resolvedHighContrast: boolean;
    resolvedThemeId: string;
}): void {
    if (typeof document === "undefined") return;

    const root = document.documentElement;
    const { styleVariables } = compileThemeStyleVariables({
        themeId: params.resolvedThemeId,
    });
    const profileVariables = compileProfileStyleVariables(
        params.contentProfile,
        params.presentationProfile,
    );

    root.dataset.theme = params.resolvedThemeId;
    root.dataset.productIconTheme = params.productIconTheme;
    root.dataset.fileIconTheme = params.fileIconTheme;
    root.dataset.contentProfile = params.contentProfile;
    root.dataset.presentationProfile = params.presentationProfile;
    root.dataset.colorScheme = params.resolvedColorScheme;
    root.dataset.contrast = params.resolvedHighContrast ? "high" : "default";
    root.style.colorScheme = params.resolvedColorScheme;
    applyStyleVariables(
        root,
        {
            ...styleVariables,
            ...profileVariables,
        },
        [...KNOWN_THEME_STYLE_VARIABLES, ...KNOWN_PROFILE_STYLE_VARIABLES],
    );
    root.classList.toggle("dark", params.resolvedColorScheme === "dark");
}

async function persistThemeUserSettings(
    patch: Record<string, unknown>,
): Promise<void> {
    try {
        await getPlatformService().settings.setValue({
            owner: "editor",
            target: "user",
            patch,
        });
        await useSettingsStore.getState().loadSnapshot();
    } catch {
        // Ignore when settings transport is not ready yet.
    }
}

